import { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const DOTS = '...';

function usePagination({
  totalRows,
  rowsPerPage,
  siblingCount = 1,
  currentPage,
}: {
  totalRows: number;
  rowsPerPage: number;
  siblingCount?: number;
  currentPage: number;
}) {
  return useMemo(() => {
    const totalPageCount = Math.ceil(totalRows / rowsPerPage);
    const totalPageNumbers = siblingCount + 5;

    if (totalPageNumbers >= totalPageCount) {
      return Array.from({ length: totalPageCount }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount
    );

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const range = (start: number, end: number) =>
      Array.from({ length: end - start + 1 }, (_, i) => i + start);

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftRange = range(1, 3 + 2 * siblingCount);
      return [...leftRange, DOTS, totalPageCount];
    }
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightRange = range(
        totalPageCount - (3 + 2 * siblingCount) + 1,
        totalPageCount
      );
      return [1, DOTS, ...rightRange];
    }
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [1, DOTS, ...middleRange, DOTS, totalPageCount];
    }
    return [];
  }, [totalRows, rowsPerPage, siblingCount, currentPage]);
}

interface DataTablePaginationProps {
  totalRows: number;
  /** 1-based page to open on, for a list restoring where the user left it. Read once at mount. */
  initialPage?: number;
  /** Page size to open with. Read once at mount. */
  initialRowsPerPage?: number;
  onPageChange: (
    upperIndex: number,
    lowerIndex: number,
    activeIndex: number,
    rowsPerPage: number
  ) => void;
}

/**
 * Pagination footer for DataTable: page size selector, range readout and page-step buttons. Opens on page 1 of 25 rows unless seeded with `initialPage` / `initialRowsPerPage`.
 *
 * Below `lg`, collapses to a single row (Previous, range readout, Next) — the
 * numbered page buttons and the rows-per-page selector are hidden to keep the
 * footer compact on phone widths. At `lg`+ the full three-group layout returns.
 *
 * @category Data
 */
function DataTablePagination({
  totalRows,
  initialPage = 1,
  initialRowsPerPage = 25,
  onPageChange,
}: DataTablePaginationProps) {
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [activeIndex, setActiveIndex] = useState(initialPage);

  const pageStep = totalRows / rowsPerPage;

  const paginationRange = usePagination({
    currentPage: activeIndex,
    totalRows,
    siblingCount: 1,
    rowsPerPage,
  });

  // A new row total or page size returns to page 1. Compared against the
  // previous values rather than run on every effect pass, so mount (and a
  // StrictMode re-run) leaves a seeded `initialPage` alone (DV-3455).
  const previousSizingRef = useRef({ totalRows, rowsPerPage });
  useEffect(() => {
    const previous = previousSizingRef.current;
    if (previous.totalRows === totalRows && previous.rowsPerPage === rowsPerPage) return;
    previousSizingRef.current = { totalRows, rowsPerPage };
    setActiveIndex(1);
  }, [totalRows, rowsPerPage]);

  useEffect(() => {
    const lowerIndex = rowsPerPage * activeIndex;
    const upperIndex = lowerIndex - rowsPerPage;
    onPageChange(upperIndex, lowerIndex, activeIndex, rowsPerPage);
  }, [rowsPerPage, activeIndex]);

  const startRow = totalRows === 0 ? 0 : (activeIndex - 1) * rowsPerPage + 1;
  const endRow = Math.min(activeIndex * rowsPerPage, totalRows);
  const rangeText =
    totalRows === 0 ? '0 of 0' : `${startRow}–${endRow} of ${totalRows}`;

  return (
    // max-lg:py-1.5 (found on-device 2026-08-21): sub-lg the page buttons are
    // 44px rather than 36px, so py-3 made the footer band ~68px — enough to
    // read as a heavy bar under the card list. 1.5 keeps 12px of breathing
    // room around a 44px control without touching the desktop band.
    <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 max-lg:py-1.5">
      <span className="text-xs text-muted-foreground max-lg:order-2">
        {rangeText}
      </span>

      <div className="contents lg:flex lg:items-center lg:gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 max-lg:h-11 max-lg:w-11 max-lg:order-1"
          disabled={activeIndex === 1}
          onClick={() => setActiveIndex(activeIndex - 1)}
          aria-label="Previous Page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <span className="hidden lg:contents">
          {paginationRange?.map((item, idx) => {
            if (item === DOTS) {
              return (
                <span
                  key={`dots-${idx}`}
                  className="px-2 text-muted-foreground"
                >
                  ...
                </span>
              );
            }
            const page = item as number;
            return (
              <Button
                key={page}
                variant={activeIndex === page ? 'default' : 'outline'}
                size="icon"
                className="h-9 w-9 text-sm max-lg:h-11 max-lg:w-11"
                onClick={() => setActiveIndex(page)}
                aria-label={`Page ${page}`}
                aria-current={activeIndex === page ? 'page' : undefined}
              >
                {page}
              </Button>
            );
          })}
        </span>

        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 max-lg:h-11 max-lg:w-11 max-lg:order-3"
          disabled={activeIndex >= pageStep}
          onClick={() => setActiveIndex(activeIndex + 1)}
          aria-label="Next Page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="hidden lg:flex lg:items-center lg:gap-2">
        <span className="text-xs text-muted-foreground">Rows</span>
        <Select
          value={rowsPerPage.toString()}
          onValueChange={(val) => {
            setRowsPerPage(parseInt(val));
            setActiveIndex(1);
          }}
        >
          <SelectTrigger className="h-9 max-lg:h-11 w-[70px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export { DataTablePagination };
export type { DataTablePaginationProps };
